import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MagnifyingGlass, Eye, X } from '@phosphor-icons/react';
import { getCateringStatusStyle } from '@/lib/statusColors';

/**
 * Admin — Corporate Catering Orders tab.
 * Encapsulates search, filter, table, and the detail modal + status updates.
 * Props:
 *  - orders: array of catering order objects
 *  - onUpdateStatus: (requestId, status) => Promise<void>
 */
export const AdminCateringTab = ({ orders, onUpdateStatus }) => {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [viewing, setViewing] = useState(null);

  const filtered = orders.filter((r) => {
    if (filter !== 'all' && r.status !== filter) return false;
    if (search.trim()) {
      const s = search.toLowerCase();
      return (
        r.request_id?.toLowerCase().includes(s) ||
        r.company_name?.toLowerCase().includes(s) ||
        r.contact_person?.toLowerCase().includes(s) ||
        r.email?.toLowerCase().includes(s)
      );
    }
    return true;
  });

  const handleStatusUpdate = async (requestId, status) => {
    await onUpdateStatus(requestId, status);
    if (viewing?.request_id === requestId) {
      setViewing({ ...viewing, status });
    }
  };

  return (
    <div className="space-y-6" data-testid="catering-management">
      <div className="bg-white border border-stone-200 shadow-sm rounded-2xl p-6">
        <h2 className="text-xl font-medium text-[#1C1917] mb-6 font-heading">Corporate Catering Orders</h2>

        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <div className="flex-1 relative">
            <MagnifyingGlass size={20} weight="duotone" className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
            <input type="text" placeholder="Search by Request ID, company, contact, email..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-12 pr-4 py-3 bg-stone-50 border border-stone-200 rounded-xl outline-none focus:border-[#1A4331]" data-testid="cat-search-input" />
          </div>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="px-4 py-3 bg-stone-50 border border-stone-200 rounded-xl outline-none focus:border-[#1A4331]" data-testid="cat-filter-select">
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="confirmed">Confirmed</option>
            <option value="completed">Completed</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        {filtered.length === 0 ? (
          <p className="text-stone-500 text-center py-12">No catering orders found</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-stone-200">
                  {['Request ID', 'Company', 'Event', 'Event Date', 'Meals', 'Total', 'Status', 'Actions'].map((h) => (
                    <th key={h} className="text-left py-3 px-3 text-xs font-bold uppercase tracking-wider text-stone-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((o) => (
                  <tr key={o.request_id} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`cat-row-${o.request_id}`}>
                    <td className="py-3 px-3 text-sm font-mono font-bold text-[#1A4331]">{o.request_id}</td>
                    <td className="py-3 px-3 text-sm">
                      <div className="font-medium text-[#1C1917]">{o.company_name}</div>
                      <div className="text-xs text-stone-500">{o.contact_person}</div>
                    </td>
                    <td className="py-3 px-3 text-sm text-stone-700">{o.event_name}</td>
                    <td className="py-3 px-3 text-sm text-stone-700">{o.event_date} {o.event_time}</td>
                    <td className="py-3 px-3 text-sm text-stone-700">{o.total_meals}</td>
                    <td className="py-3 px-3 text-sm font-bold text-[#1A4331]">₹{o.total?.toLocaleString()}</td>
                    <td className="py-3 px-3">
                      <span data-testid={`cat-status-badge-${o.request_id}`} className={`text-xs px-3 py-1 rounded-full font-medium ${getCateringStatusStyle(o.status)}`}>{o.status}</span>
                    </td>
                    <td className="py-3 px-3">
                      <button onClick={() => setViewing(o)} className="text-stone-600 hover:text-[#1A4331] p-1.5 rounded-lg hover:bg-stone-100" title="View" data-testid={`view-cat-${o.request_id}`}>
                        <Eye size={18} weight="duotone" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {viewing && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={() => setViewing(null)} data-testid="catering-detail-modal">
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500">Request ID</p>
                  <p className="text-2xl font-bold text-[#1A4331]">{viewing.request_id}</p>
                </div>
                <button onClick={() => setViewing(null)} className="text-stone-500 hover:text-stone-800 p-2"><X size={24} weight="bold" /></button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-stone-50 rounded-xl p-4">
                    <p className="text-xs text-stone-500 uppercase tracking-wider mb-1">Company</p>
                    <p className="font-medium text-[#1C1917]">{viewing.company_name}</p>
                    <p className="text-sm text-stone-600 mt-1">{viewing.contact_person}</p>
                  </div>
                  <div className="bg-stone-50 rounded-xl p-4">
                    <p className="text-xs text-stone-500 uppercase tracking-wider mb-1">Contact</p>
                    <p className="font-medium text-[#1C1917]">{viewing.mobile}</p>
                    <p className="text-sm text-stone-600 mt-1">{viewing.email}</p>
                  </div>
                </div>

                <div className="bg-stone-50 rounded-xl p-4">
                  <p className="text-xs text-stone-500 uppercase tracking-wider mb-2">Event Details</p>
                  <p className="font-medium text-[#1C1917]">{viewing.event_name}</p>
                  <p className="text-sm text-stone-600">{viewing.event_date} at {viewing.event_time}</p>
                  <p className="text-sm text-stone-600 mt-1">{viewing.delivery_address}, {viewing.city}, {viewing.state} - {viewing.zipcode}</p>
                  {viewing.special_instructions && <p className="text-sm text-stone-700 mt-2 italic">Note: {viewing.special_instructions}</p>}
                </div>

                <div className="bg-stone-50 rounded-xl p-4">
                  <p className="text-xs text-stone-500 uppercase tracking-wider mb-2">Ordered Items</p>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-stone-500 border-b border-stone-200"><th className="text-left py-2">Item</th><th className="text-center py-2">Qty</th><th className="text-right py-2">Price</th><th className="text-right py-2">Subtotal</th></tr>
                    </thead>
                    <tbody>
                      {viewing.items?.map((i, idx) => (
                        <tr key={`${i.id || i.name}-${idx}`} className="border-b border-stone-100">
                          <td className="py-2">{i.name}</td>
                          <td className="text-center py-2">{i.quantity}</td>
                          <td className="text-right py-2">₹{i.price}</td>
                          <td className="text-right py-2 font-medium">₹{(i.price * i.quantity).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr><td colSpan="3" className="text-right py-1">Subtotal</td><td className="text-right py-1 font-medium">₹{Math.round(viewing.subtotal || 0).toLocaleString()}</td></tr>
                      <tr><td colSpan="3" className="text-right py-1">GST (5%)</td><td className="text-right py-1 font-medium">₹{Math.round(viewing.gst || 0).toLocaleString()}</td></tr>
                      <tr><td colSpan="3" className="text-right py-2 font-bold text-[#1A4331]">Grand Total</td><td className="text-right py-2 font-bold text-[#1A4331]">₹{Math.round(viewing.total || 0).toLocaleString()}</td></tr>
                    </tfoot>
                  </table>
                </div>

                <div className="border-t border-stone-200 pt-4">
                  <p className="text-xs text-stone-500 uppercase tracking-wider mb-3">Update Status</p>
                  <div className="flex flex-wrap gap-2">
                    {['pending', 'accepted', 'confirmed', 'completed', 'rejected'].map((s) => (
                      <button key={s} onClick={() => handleStatusUpdate(viewing.request_id, s)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${viewing.status === s ? 'bg-[#1A4331] text-white' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'}`} data-testid={`cat-status-btn-${s}`}>
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default AdminCateringTab;
